import React, { useState } from "react";
import { useProject } from "../contexts/ProjectContext";
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Plus, Edit3, Trash2, Save, X, Bookmark } from "lucide-react";
import { useToast } from "../hooks/use-toast";

const FootnoteManager = () => {
  const { footnotes, createFootnote, updateFootnote, deleteFootnote } = useProject();
  const { toast } = useToast();
  
  const [isCreating, setIsCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ name: "", content: "" });

  const handleStartCreate = () => {
    setFormData({ name: "", content: "" });
    setIsCreating(true);
  };

  const handleStartEdit = (footnote) => {
    setFormData({ name: footnote.name, content: footnote.content });
    setEditingId(footnote.id);
  };

  const handleSave = async () => {
    if (!formData.name.trim() || !formData.content.trim()) {
      toast({
        title: "Error",
        description: "Please fill in both name and content fields",
        variant: "destructive"
      });
      return;
    }

    try {
      if (isCreating) {
        await createFootnote(formData);
        toast({
          title: "Success",
          description: "Footnote created successfully"
        });
      } else {
        await updateFootnote(editingId, formData);
        toast({
          title: "Success",
          description: "Footnote updated successfully"
        });
      }
      
      setIsCreating(false);
      setEditingId(null);
      setFormData({ name: "", content: "" });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save footnote",
        variant: "destructive"
      });
    }
  };

  const handleCancel = () => {
    setIsCreating(false);
    setEditingId(null);
    setFormData({ name: "", content: "" });
  };

  const handleDelete = async (footnoteId, footnoteName) => {
    if (window.confirm(`Are you sure you want to delete "${footnoteName}"?`)) {
      try {
        await deleteFootnote(footnoteId);
        toast({
          title: "Success",
          description: "Footnote deleted successfully"
        });
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to delete footnote",
          variant: "destructive"
        });
      }
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <Bookmark className="w-5 h-5 mr-2 text-blue-500" />
          Footnote Management
        </CardTitle>
        <CardDescription>
          Create and manage reusable text snippets to quickly append to your prompts
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Create/Edit Form */}
        {(isCreating || editingId) && (
          <div className="p-4 bg-slate-50 rounded-lg border">
            <h4 className="font-medium text-slate-700 mb-3">
              {isCreating ? "Create New Footnote" : "Edit Footnote"}
            </h4>
            <div className="space-y-3">
              <div>
                <Label htmlFor="footnote-name">Name *</Label>
                <Input
                  id="footnote-name"
                  placeholder="e.g., Python Path (VS Code)"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                />
              </div>
              <div>
                <Label htmlFor="footnote-content">Content *</Label>
                <Textarea
                  id="footnote-content"
                  placeholder="e.g., Note: Use python.exe path: C:\Python311\python.exe"
                  rows={3}
                  value={formData.content}
                  onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                />
                <p className="text-xs text-slate-500 mt-1">
                  This text will be appended to the end of prompts when selected
                </p>
              </div>
              <div className="flex space-x-2">
                <Button onClick={handleSave} size="sm">
                  <Save className="w-4 h-4 mr-2" />
                  Save
                </Button>
                <Button onClick={handleCancel} variant="outline" size="sm">
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Add New Button */}
        {!isCreating && !editingId && (
          <Button onClick={handleStartCreate} variant="outline" className="w-full">
            <Plus className="w-4 h-4 mr-2" />
            Add New Footnote
          </Button>
        )}

        {/* Footnotes List */}
        {footnotes.length > 0 ? (
          <div className="space-y-3">
            <h4 className="font-medium text-slate-700">Saved Footnotes</h4>
            <div className="space-y-2">
              {footnotes.map((footnote) => (
                <div
                  key={footnote.id}
                  className="p-3 bg-white border rounded-lg hover:shadow-sm transition-shadow"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h5 className="font-medium text-slate-700 text-sm truncate">
                        {footnote.name}
                      </h5>
                      <p className="text-xs text-slate-500 mt-1 line-clamp-2">
                        {footnote.content}
                      </p>
                      <p className="text-xs text-slate-400 mt-2">
                        Created: {new Date(footnote.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex space-x-1 ml-3">
                      <Button
                        onClick={() => handleStartEdit(footnote)}
                        variant="ghost"
                        size="sm"
                        disabled={isCreating || editingId}
                      >
                        <Edit3 className="w-3 h-3" />
                      </Button>
                      <Button
                        onClick={() => handleDelete(footnote.id, footnote.name)}
                        variant="ghost"
                        size="sm"
                        disabled={isCreating || editingId}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : !isCreating && (
          <div className="text-center py-8 text-slate-500">
            <Bookmark className="w-8 h-8 mx-auto mb-2 text-slate-400" />
            <p className="text-sm">No footnotes created yet</p>
            <p className="text-xs mt-1">Create your first footnote to get started</p>
          </div>
        )}

        {/* Help Text */}
        <div className="text-xs text-slate-600 bg-blue-50 p-3 rounded-lg border border-blue-200">
          <h4 className="font-medium text-blue-800 mb-1">💡 How to use footnotes:</h4>
          <ul className="space-y-1">
            <li>• Create reusable text snippets with helpful names</li>
            <li>• Use them to quickly add common notes to your prompts</li>
            <li>• Examples: file paths, command preferences, coding standards</li>
            <li>• Footnotes appear in the prompt editor sidebar for easy access</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};

export default FootnoteManager;